import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, tap, of, catchError, switchMap, map, shareReplay, finalize } from 'rxjs';
import { environment } from '../../environments/environment';
import { LOCAL_ERROR_HANDLED } from './http-request-context';
import { UserRole, UserStatus } from './types';

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  displayName: string;
  preferredCurrency: string;
  preferredLanguage: string;
  creditBalanceHours: number;
  profileLocked: boolean;
  company?: string | null;
  description?: string | null;
  referralSource?: string | null;
  wgPublicKey?: string | null;
  wgKeySource?: string | null;
  wgPeerIp?: string | null;
  sshPublicKey?: string | null;
  sshKeySource?: string | null;
  profileCompleted?: boolean;
  oauthProvider?: string | null;
  consentRequired?: boolean;
  privacyConsentVersion?: string | null;
  privacyPolicyVersion?: string | null;
}

type UserSettingsUpdate = Partial<Pick<AuthUser, 'preferredCurrency' | 'preferredLanguage'>>;

interface LoginCredentials {
  email: string;
  password: string;
}

interface RegisterCredentials {
  email: string;
  password: string;
  displayName: string;
  acceptPrivacyPolicy: boolean;
  preferredLanguage?: string;
}

interface RegisterResponse {
  message: string;
}

interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  message?: string;
}

interface LoginErrorResponse {
  error?: string;
  code?: string;
  message?: string;
}

interface ProfileCompletionPayload {
  displayName?: string;
  company?: string | null;
  description: string;
  referralSource?: string | null;
}

interface ProfileCompletionResponse {
  message: string;
  user: AuthUser;
}

interface UserSettingsResponse {
  message: string;
  user: AuthUser;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly SESSION_HINT_STORAGE_KEY = 'auth-session-hint';
  private static readonly LEGACY_TOKEN_STORAGE_KEY = 'auth-access-token';

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly tokenSubject = new BehaviorSubject<string | null>(null);
  private readonly userSubject = new BehaviorSubject<AuthUser | null>(null);
  private readonly authMessageSubject = new BehaviorSubject<string | null>(null);
  private readonly authCodeSubject = new BehaviorSubject<string | null>(null);
  private refreshRequest$: Observable<LoginResponse | null> | null = null;

  readonly token$: Observable<string | null> = this.tokenSubject.asObservable();
  readonly currentUser$: Observable<AuthUser | null> = this.userSubject.asObservable();
  readonly user$: Observable<AuthUser | null> = this.currentUser$;
  readonly authMessage$: Observable<string | null> = this.authMessageSubject.asObservable();
  readonly authCode$: Observable<string | null> = this.authCodeSubject.asObservable();

  constructor() {
    // The access token is kept in memory only (XSS hardening, gh #242); any
    // token left in localStorage by older builds is worthless after 900 s but
    // is scrubbed so it cannot be replay-read.
    localStorage.removeItem(AuthService.LEGACY_TOKEN_STORAGE_KEY);
  }

  public login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials, { withCredentials: true }).pipe(
      tap((response) => {
        this.setAuthSession(response.accessToken);
        this.setAuthFeedback(null, null);
      }),
      switchMap((response) => this.getUser().pipe(map(() => response)))
    );
  }

  public register(credentials: RegisterCredentials): Observable<RegisterResponse> {
    return this.http.post<RegisterResponse>(`${environment.apiUrl}/auth/register`, credentials);
  }

  public verifyEmail(token: string): Observable<LoginResponse> {
    return this.http.get<LoginResponse>(`${environment.apiUrl}/auth/verify-email/${encodeURIComponent(token)}`, { withCredentials: true }).pipe(
      tap((response) => {
        this.setAuthSession(response.accessToken);
        this.setAuthFeedback(response.message ?? null, null);
      }),
      switchMap((response) => this.getUser().pipe(map(() => response)))
    );
  }

  public completeProfile(payload: ProfileCompletionPayload): Observable<ProfileCompletionResponse> {
    return this.http.put<ProfileCompletionResponse>(`${environment.apiUrl}/users/me/profile`, payload).pipe(
      tap((response) => {
        this.userSubject.next(response.user);
        this.setAuthFeedback(response.message, 'pending_approval');
      })
    );
  }

  public acceptPrivacyConsent(): Observable<AuthUser> {
    return this.http
      .post<{ message: string; user: AuthUser }>(`${environment.apiUrl}/users/me/privacy-consent`, { accept: true })
      .pipe(
        map((response) => response.user),
        tap((user) => {
          this.userSubject.next(user);
        })
      );
  }

  public redirectToPrivacyConsent(): void {
    void this.router.navigate(['/auth/privacy-consent']);
  }

  public applyAuthError(error: LoginErrorResponse): void {
    this.clearSession(false);
    this.setAuthFeedback(error.message ?? 'Authentication failed.', error.code ?? null);
  }

  public clearAuthFeedback(): void {
    this.setAuthFeedback(null, null);
  }

  public logout(): void {
    this.clearSession(true);

    this.http.post(`${environment.apiUrl}/auth/logout`, null, {
      withCredentials: true,
      context: new HttpContext().set(LOCAL_ERROR_HANDLED, true),
    }).subscribe({
      error: () => {},
    });

    void this.router.navigate(['/']);
  }

  public setToken(token: string): void {
    this.setAuthSession(token);
  }

  public getTokenExpiryTimestamp(token = this.tokenSubject.value): number | null {
    if (!token) {
      return null;
    }

    const payload = this.decodeJwtPayload(token);
    if (typeof payload?.exp !== 'number') {
      return null;
    }

    return payload.exp * 1000;
  }

  public refreshAccessToken(): Observable<LoginResponse | null> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const request$ = this.http.post<LoginResponse>(`${environment.apiUrl}/auth/refresh`, null, {
      withCredentials: true,
      context: new HttpContext().set(LOCAL_ERROR_HANDLED, true),
    }).pipe(
      tap((response) => {
        this.setAuthSession(response.accessToken);
      }),
      catchError((error) => {
        if (!this.isTransientAuthError(error)) {
          this.clearSession(false);
        }
        return of(null);
      }),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    this.refreshRequest$ = request$;
    return request$;
  }

  public shouldRefreshWithin(thresholdMs: number): boolean {
    const expiry = this.getTokenExpiryTimestamp();
    return expiry !== null && expiry - Date.now() <= thresholdMs;
  }

  public setDevSession(user: AuthUser, token = 'dev-token'): void {
    this.setAuthSession(token);
    this.userSubject.next(user);
    this.setAuthFeedback(null, null);
  }

  public updateCurrentUserSettings(settings: UserSettingsUpdate): void {
    const user = this.userSubject.value;
    if (!user) {
      return;
    }

    this.userSubject.next({
      ...user,
      ...settings,
    });
  }

  public updateUserSettings(settings: UserSettingsUpdate, context?: HttpContext): Observable<AuthUser> {
    return this.http.put<UserSettingsResponse>(`${environment.apiUrl}/users/me/settings`, settings, context ? { context } : undefined).pipe(
      map((response) => response.user),
      tap((user) => {
        this.userSubject.next(user);
      })
    );
  }

  public changePassword(currentPassword: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/users/me/change-password`, { currentPassword, newPassword });
  }

  public requestPasswordReset(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  public resetPassword(token: string, password: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/reset-password`, { token, password });
  }

  public getToken(): string | null {
    return this.tokenSubject.value;
  }

  public getUser(): Observable<AuthUser | null> {
    const token = this.tokenSubject.value;
    if (!token) {
      return of(null);
    }

    return this.http.get<AuthUser>(`${environment.apiUrl}/users/me`).pipe(
      tap((user) => this.userSubject.next(user)),
      catchError((error) => {
        if (!this.isTransientAuthError(error)) {
          this.clearSession(false);
        }
        return of(null);
      })
    );
  }

  public isAuthenticated(): boolean {
    return this.tokenSubject.value !== null;
  }

  public isOwner(): boolean {
    return this.userSubject.value?.role === UserRole.Owner;
  }

  public get currentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  public refreshCurrentUser(): Observable<AuthUser | null> {
    return this.getUser();
  }

  private setAuthSession(token: string): void {
    this.tokenSubject.next(token);
    this.setSessionHint();
  }

  private decodeJwtPayload(token: string): { exp?: number } | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    try {
      const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
      const json = atob(`${normalized}${padding}`);
      return JSON.parse(json) as { exp?: number };
    } catch {
      return null;
    }
  }

  private isTransientAuthError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    if (error.status === 0 || error.status === 408 || error.status === 429) {
      return true;
    }

    return error.status >= 500;
  }

  private clearSession(clearFeedback: boolean): void {
    this.clearSessionHint();
    this.tokenSubject.next(null);
    this.userSubject.next(null);

    if (clearFeedback) {
      this.setAuthFeedback(null, null);
    }
  }

  private setAuthFeedback(message: string | null, code: string | null): void {
    this.authMessageSubject.next(message);
    this.authCodeSubject.next(code);
  }

  /**
   * Restores an authenticated session after a page reload without touching
   * the access token (it is never persisted). Relies on the HttpOnly refresh
   * cookie: when a session hint is present, a silent refresh exchanges the
   * cookie for a fresh in-memory access token and rehydrates the user.
   */
  public restoreSession(): Observable<boolean> {
    if (this.tokenSubject.value) {
      return of(true);
    }

    if (!this.hasSessionHint()) {
      return of(false);
    }

    return this.refreshAccessToken().pipe(
      switchMap((response) => {
        if (!response) {
          return of(false);
        }

        return this.getUser().pipe(
          map((user) => {
            if (user) {
              return true;
            }

            // A transient /users/me failure after a successful refresh would
            // leave a token in memory with no user; drop the token so the boot
            // ends in a consistent guest state. The hint survives, so the next
            // reload retries the silent refresh. Definitive failures already
            // cleared the session inside getUser().
            this.tokenSubject.next(null);
            return false;
          }),
        );
      }),
    );
  }

  private setSessionHint(): void {
    localStorage.setItem(AuthService.SESSION_HINT_STORAGE_KEY, '1');
  }

  private hasSessionHint(): boolean {
    return localStorage.getItem(AuthService.SESSION_HINT_STORAGE_KEY) === '1';
  }

  private clearSessionHint(): void {
    localStorage.removeItem(AuthService.SESSION_HINT_STORAGE_KEY);
  }
}
